import React, { useMemo } from 'react';
import {
  ScrollView,
  Text,
  View,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';

interface TextRendererProps {
  fileUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  fileName?: string;
}

/**
 * Renders a text/plain attachment as scrollable, escaped text — NOT through the
 * data: URL / <img> path images and PDFs use. React <Text> escapes text nodes,
 * so any pasted markup renders as inert text. (Story 7.73 / #1149, D5/D6)
 */
const decodeTextFromDataUrl = (fileUrl?: string): string => {
  if (!fileUrl) {
    return '';
  }
  const commaIndex = fileUrl.indexOf(',');
  const base64 = commaIndex >= 0 ? fileUrl.slice(commaIndex + 1) : fileUrl;
  try {
    // atob yields a latin1 binary string; decodeURIComponent(escape(...))
    // recovers the original UTF-8 (smart quotes, em dashes, etc.).

    return decodeURIComponent(escape(atob(base64)));
  } catch {
    return '';
  }
};

export const TextRenderer: React.FC<TextRendererProps> = ({
  fileUrl,
  isLoading,
  hasError,
  fileName,
}) => {
  const text = useMemo(() => decodeTextFromDataUrl(fileUrl), [fileUrl]);

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#1C3A70" />
      </View>
    );
  }

  if (hasError) {
    return (
      <View style={styles.center}>
        <Text style={styles.error}>Unable to load {fileName || 'text'}.</Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.content}
      testID="attachment-text-viewer"
    >
      <Text style={styles.text} selectable>
        {text}
      </Text>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  error: { color: '#C0392B', fontSize: 14 },
  scroll: { flex: 1, backgroundColor: '#FFFFFF' },
  content: { padding: 16 },
  text: { fontSize: 14, lineHeight: 20, color: '#1a1a1a' },
});
